import { Checkbox, Switch, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';

import { useUpdateUserAccessRightsMutation } from '@app/api/AccessRights';
import { FRONT_WARNING, SUCCESS } from '@app/constants';
import {
  useAccessRightsToAi,
  useAccessToOrganizations,
  useContentWidth,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import type { UseAccessRightsListProps } from '@app/pages/Users/AccessRightsTab/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useGetModulesRightsQuery } from '@app/services/rtkQuery/organization';
import { useErrorHandler } from '@app/shared/hooks';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type {
  AccessRightsModules,
  UserAccessRightsInterface,
} from '@app/types/NewAccessRight';

import { accordionList } from '@pages/Users/AccessRightsTab/data/constants';
import { checkAllowedModule } from '@pages/Users/AccessRightsTab/utils/checkAllowedModule';

import type { ItemType } from 'rc-collapse/es/interface';

const PF = 'access-rights-list';

const { Text } = Typography;

export const useAccessRightsList = ({
  editableUserAccessRights,
  editableUserUnsavedAccessRights,
  setEditableUserUnsavedAccessRights,
  setEditableUserAccessRights,
}: UseAccessRightsListProps) => {
  const [hasChanges, setHasChanges] = useState(false);
  const { showNotification } = useNotification();
  const {
    activeBranchId,
    params: { ActiveBranchId },
  } = useUserPrint();
  const { contentWidth } = useContentWidth();
  const { userAccessRights } = useNewUserAccessRights();
  const { hasOrganizationRightToAi } = useAccessRightsToAi();
  const { areRightsToOrgsInSettings } = useAccessToOrganizations();
  const {
    data: modulesRights,
    isError: isModulesRightsError,
    isFetching: isModulesRightsFetching,
    isLoading: isModulesRightsLoading,
    error: modulesRightsError,
  } = useGetModulesRightsQuery({
    ActiveBranchId,
  });

  useErrorHandler([
    {
      flag: isModulesRightsError,
      error: modulesRightsError,
    },
  ]);

  const [
    updateUserAccessRights,
    { isLoading: isUpdateUserAccessRightsLoading },
  ] = useUpdateUserAccessRightsMutation();

  const addQuotes = useCallback(
    (value: string, isSection: boolean) =>
      isSection ? `Раздел «${value}»` : value,
    []
  );

  const isAdmin = useMemo(() => {
    if (!editableUserUnsavedAccessRights) return false;

    const { IsAdmin, IsSuperAdmin } = editableUserUnsavedAccessRights;

    return IsAdmin || IsSuperAdmin;
  }, [editableUserUnsavedAccessRights]);

  const isCurrentUserAdmin = useMemo(() => {
    if (!userAccessRights) return false;
    const { IsAdmin, IsSuperAdmin } = userAccessRights;
    return IsAdmin || IsSuperAdmin;
  }, [userAccessRights]);

  const showCreateTemplateButton = useMemo(() => {
    const {
      AccessRights: { IsCreateTemplates },
    } = userAccessRights;

    return IsCreateTemplates;
  }, [userAccessRights]);

  const isClientModuleActive = useMemo(() => {
    if (!editableUserUnsavedAccessRights) return false;

    const {
      Clients: { IsOn },
    } = editableUserUnsavedAccessRights;
    return IsOn;
  }, [editableUserUnsavedAccessRights]);

  const hasRightOnlyAvailableRights = useMemo(() => {
    if (!userAccessRights) return false;

    const {
      AccessRights: { EditingRightsWithinOpenRights },
    } = userAccessRights;

    return EditingRightsWithinOpenRights;
  }, [userAccessRights]);

  const isDesktop = useMemo(() => contentWidth > 450, [contentWidth]);

  const isModuleGrantRestricted = useCallback(
    (key: keyof AccessRightsModules) => {
      if (!userAccessRights) return false;
      if (isCurrentUserAdmin) return false;
      if (!hasRightOnlyAvailableRights) return false;

      return !userAccessRights[key]?.IsOn;
    },
    [userAccessRights, isCurrentUserAdmin, hasRightOnlyAvailableRights]
  );

  const getSwitchTooltip = useCallback(
    (
      key: keyof AccessRightsModules,
      tooltip: string,
      allowedModule: boolean
    ) => {
      if (!editableUserUnsavedAccessRights) return false;

      if (!allowedModule) {
        return 'Модуль недоступен для текущей организации';
      }

      switch (key) {
        case 'Leads':
          if (!isClientModuleActive) return tooltip;
          break;
        default:
          if (tooltip) return tooltip;
      }

      if (isModuleGrantRestricted(key)) {
        return 'У вас нет прав на выдачу этого модуля прав доступа другому пользователю';
      }

      return '';
    },
    [
      editableUserUnsavedAccessRights,
      isClientModuleActive,
      isModuleGrantRestricted,
    ]
  );

  const getCustomSwitchDisabled = useCallback(
    (key: keyof AccessRightsModules) => {
      switch (key) {
        case 'Leads':
          return !isClientModuleActive;
        default:
          return false;
      }
    },
    [isClientModuleActive]
  );

  const getDescription = useCallback(
    (key: keyof AccessRightsModules) => {
      if (!editableUserUnsavedAccessRights) return '';

      switch (key) {
        case 'Leads':
          return generateSpanWithDataTestId(
            'Для видимости лидов пользователя необходимо добавить социальные интеграции на пользователя в его карточке.',
            'leads-hint'
          );

        case 'AiOperator':
          return generateSpanWithDataTestId(
            'Обратите внимание, без включенного модуля «История действий» не будет работать история действий ИИ-агента.',
            'ai-operator-hint'
          );
        default:
          return '';
      }
    },
    [editableUserUnsavedAccessRights]
  );

  const getSwitchDisabled = useCallback(
    (key: keyof AccessRightsModules) => {
      if (!userAccessRights) return true;
      if (!editableUserUnsavedAccessRights) return true;

      const customDisabled = getCustomSwitchDisabled(key);

      if (customDisabled) return customDisabled;

      return isModuleGrantRestricted(key);
    },
    [
      userAccessRights,
      editableUserUnsavedAccessRights,
      getCustomSwitchDisabled,
      isModuleGrantRestricted,
    ]
  );

  const getCheckboxDisabled = useCallback(
    <
      ModuleKey extends keyof AccessRightsModules,
      RightKey extends Exclude<keyof AccessRightsModules[ModuleKey], 'IsOn'>
    >(
      moduleKey: ModuleKey,
      checkboxKey: RightKey
    ): boolean => {
      checkboxKey;
      if (!userAccessRights) return true;
      if (!editableUserUnsavedAccessRights) return true;
      const isActiveModule = editableUserUnsavedAccessRights[moduleKey]?.IsOn;
      if (!isActiveModule) return true;
      if (isCurrentUserAdmin) return false;
      if (!hasRightOnlyAvailableRights) return false;

      const isCurrentUserHasModuleRight = userAccessRights[moduleKey]?.IsOn;

      return !isCurrentUserHasModuleRight;
    },
    [
      userAccessRights,
      editableUserUnsavedAccessRights,
      hasRightOnlyAvailableRights,
      isCurrentUserAdmin,
    ]
  );

  const getIsActiveSwitch = useCallback(
    (key: keyof AccessRightsModules) => {
      if (!editableUserUnsavedAccessRights) return false;

      // ?. — на случай, если бэк стенда не отдаёт блок модуля (в стейт кладётся
      // ответ GetUserAccessRights / ApplyAccessRightsTemplate как есть, без
      // слияния с дефолтами).
      const isActive = editableUserUnsavedAccessRights[key]?.IsOn;

      return Boolean(isActive);
    },
    [editableUserUnsavedAccessRights]
  );

  const getIsActiveCheckbox = useCallback(
    <
      ModuleKey extends keyof AccessRightsModules,
      RightKey extends Exclude<keyof AccessRightsModules[ModuleKey], 'IsOn'>
    >(
      moduleKey: ModuleKey,
      checkboxKey: RightKey
    ): boolean => {
      if (!editableUserUnsavedAccessRights) return false;

      return Boolean(editableUserUnsavedAccessRights[moduleKey]?.[checkboxKey]);
    },
    [editableUserUnsavedAccessRights]
  );

  const onCustomChangeSwitch = useCallback(
    (moduleKey: keyof AccessRightsModules, checked: boolean) => {
      switch (moduleKey) {
        case 'Clients':
          if (!checked) {
            setEditableUserUnsavedAccessRights((prev) => {
              if (!prev) return prev;

              const updatedAccessRights = {
                ...prev,
                Leads: {
                  ...prev.Leads,
                  IsOn: false,
                },
              };

              return updatedAccessRights;
            });
            break;
          }
          break;
        default:
          break;
      }
    },
    [setEditableUserUnsavedAccessRights]
  );

  const onChangeSwitch = useCallback(
    (moduleKey: keyof AccessRightsModules, checked: boolean) => {
      if (!editableUserUnsavedAccessRights) return;

      const currentModule = { ...editableUserUnsavedAccessRights[moduleKey] };

      const keys = Object.keys(currentModule);

      if (!checked) {
        keys.forEach((key) => {
          currentModule[key as keyof typeof currentModule] = checked;
        });
      }

      const updatedAccessRights = {
        ...editableUserUnsavedAccessRights,
        [moduleKey]: {
          ...currentModule,
          IsOn: checked,
        },
      };

      setEditableUserUnsavedAccessRights(updatedAccessRights);
      onCustomChangeSwitch(moduleKey, checked);
    },
    [
      editableUserUnsavedAccessRights,
      onCustomChangeSwitch,
      setEditableUserUnsavedAccessRights,
    ]
  );

  const onChangeCheckbox = useCallback(
    <
      ModuleKey extends keyof AccessRightsModules,
      RightKey extends Exclude<keyof AccessRightsModules[ModuleKey], 'IsOn'>
    >(
      moduleKey: ModuleKey,
      checkboxKey: RightKey,
      checked: boolean
    ) => {
      if (!editableUserUnsavedAccessRights) return;

      const currentModule = { ...editableUserUnsavedAccessRights[moduleKey] };

      const updatedAccessRights = {
        ...editableUserUnsavedAccessRights,
        [moduleKey]: {
          ...currentModule,
          [checkboxKey]: checked,
        },
      };

      setEditableUserUnsavedAccessRights(updatedAccessRights);
    },
    [editableUserUnsavedAccessRights, setEditableUserUnsavedAccessRights]
  );

  const formattedAccordionList = useMemo(() => {
    const filteredAccordionList = accordionList.filter(({ key }) => {
      switch (key) {
        case 'AiOperator':
          return hasOrganizationRightToAi;
        case 'Organizations':
          return areRightsToOrgsInSettings;
        default:
          return true;
      }
    });

    const enabled: ItemType[] = [];
    const disabledArr: ItemType[] = [];

    filteredAccordionList.forEach(
      ({
        key: moduleKey,
        items,
        label,
        isSection,
        tooltip = '',
        tooltipDataTestId = '',
        dataTestId,
      }) => {
        const isActiveSwitch = getIsActiveSwitch(moduleKey);
        const description = getDescription(moduleKey);

        const isEmpty = items.length === 0 && !description;
        const allowedModule = checkAllowedModule(
          moduleKey,
          modulesRights?.Modules
        );

        const disabled = getSwitchDisabled(moduleKey) || !allowedModule;

        const item: ItemType = {
          key: moduleKey,
          label: (
            <span
              className={cn({ 'cursor-default': isEmpty })}
              data-testid={dataTestId}
            >
              {addQuotes(label, isSection)}
            </span>
          ),
          extra: (
            <Tooltip
              title={getSwitchTooltip(moduleKey, tooltip, allowedModule)}
              data-testid={tooltipDataTestId}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={isActiveSwitch}
                  disabled={disabled}
                  onChange={(checked) => onChangeSwitch(moduleKey, checked)}
                  data-testid={
                    isActiveSwitch ? 'switch-active' : 'switch-not-active'
                  }
                />
              </div>
            </Tooltip>
          ),
          children: isEmpty ? null : (
            <div className={`${PF}_children`}>
              {!!description && <Text>{description}</Text>}
              <div className={`${PF}_checkboxes`}>
                {items.map(({ key, label, dataTestId = '' }) => {
                  const checkboxKey = key as Exclude<
                    keyof AccessRightsModules[typeof moduleKey],
                    'IsOn'
                  >;

                  return (
                    <Checkbox
                      key={key}
                      checked={getIsActiveCheckbox(moduleKey, checkboxKey)}
                      onChange={(e) =>
                        onChangeCheckbox(
                          moduleKey,
                          checkboxKey,
                          e.target.checked
                        )
                      }
                      disabled={getCheckboxDisabled(moduleKey, checkboxKey)}
                      data-testid="checkbox-rights"
                    >
                      {generateSpanWithDataTestId(label, dataTestId)}
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          ),
          collapsible: isEmpty ? 'disabled' : undefined,
          className: cn({
            'cursor-default': isEmpty,
          }),
          classNames: {
            header: cn({
              [`${PF}_header_is-empty`]: isEmpty,
            }),
          },
        };

        if (disabled) {
          disabledArr.push(item);
        } else {
          enabled.push(item);
        }
      }
    );

    return [...enabled, ...disabledArr];
  }, [
    addQuotes,
    getIsActiveSwitch,
    getSwitchDisabled,
    getSwitchTooltip,
    getDescription,
    getIsActiveCheckbox,
    getCheckboxDisabled,
    onChangeSwitch,
    onChangeCheckbox,
    hasOrganizationRightToAi,
    areRightsToOrgsInSettings,
    modulesRights,
  ]);

  const onChangeEditableUserUnsavedAccessRights = useCallback(
    (newAccessRights: UserAccessRightsInterface) => {
      setEditableUserUnsavedAccessRights(newAccessRights);
    },
    [setEditableUserUnsavedAccessRights]
  );

  const checkHasChanges = useCallback(() => {
    if (!editableUserAccessRights || !editableUserUnsavedAccessRights) {
      return setHasChanges(false);
    }
    const hasChanges = !isEqual(
      editableUserAccessRights,
      editableUserUnsavedAccessRights
    );
    setHasChanges(hasChanges);
  }, [editableUserAccessRights, editableUserUnsavedAccessRights]);

  const updateUserAccessRightsHandler = useCallback(async () => {
    if (!editableUserUnsavedAccessRights) {
      return showNotification(
        'Не удалось получить права доступа пользователя',
        FRONT_WARNING
      );
    }
    if (!activeBranchId) {
      return showNotification(
        'Не удалось получить идентификатор активного филиала',
        FRONT_WARNING
      );
    }
    try {
      const response = await updateUserAccessRights({
        data: editableUserUnsavedAccessRights,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setEditableUserAccessRights(response);
      setEditableUserUnsavedAccessRights(response);
      showNotification('Права доступа обновлены', SUCCESS);
      setHasChanges(false);
    } catch (error) {
      showNotification(error);
    }
  }, [
    editableUserUnsavedAccessRights,
    activeBranchId,
    showNotification,
    updateUserAccessRights,
    setEditableUserAccessRights,
    setEditableUserUnsavedAccessRights,
  ]);

  return {
    hasChanges,
    isAdmin,
    onChangeEditableUserUnsavedAccessRights,
    isDesktop,
    showCreateTemplateButton,
    checkHasChanges,
    updateUserAccessRightsHandler,
    isUpdateUserAccessRightsLoading,
    formattedAccordionList,
    isModulesRightsFetching,
    isModulesRightsLoading,
  };
};
